import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  ViewChild,
  inject
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive
} from '@angular/router';
import { BlockScrollStrategy } from '@angular/cdk/overlay';
import { filter } from 'rxjs';

import { MediaQueryService } from '@core/breakpoints';
import { DropdownContent, DropdownDirective, DropdownToggle } from '@core/dropdown';
import { AuthState } from '@app/modules/users';

@Component({
  selector: 'shell-header-burger-menu',
  templateUrl: './header-burger-menu.component.html',
  styleUrl: './header-burger-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    RouterLinkActive,
    DropdownContent,
    DropdownDirective,
    DropdownToggle
  ]
})
export class HeaderBurgerMenuComponent {
  private readonly router = inject(Router);
  private readonly scrollBlock = inject(BlockScrollStrategy);
  private readonly destroyRef = inject(DestroyRef);
  private readonly mediaService = inject(MediaQueryService);

  public readonly authState = inject(AuthState);
  public isSm = toSignal(this.mediaService.mediaQuery('max', 'sm'));

  @Input()
  public isLogged!: boolean;

  @ViewChild(DropdownDirective)
  set _dropdown(dropdown: DropdownDirective|undefined) {
    if(!dropdown || this.dropdown) {
      return;
    }

    this.dropdown = dropdown;
    this.setupScrollBlockListener();
  }

  private dropdown?: DropdownDirective;

  constructor() {
    this.listenRouterEvents();
  }

  logout(): void {
    this.dropdown && this.dropdown.close();
    this.authState.logout();
  }

  private listenRouterEvents(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.dropdown && this.dropdown.close();
      });
  }

  private setupScrollBlockListener(): void {
    if(!this.isSm()) {
      return;
    }

    this.dropdown!.isOpen$
      .pipe( takeUntilDestroyed(this.destroyRef) )
      .subscribe(isOpen => {
        if(isOpen) {
          this.disableScroll();
        } else {
          this.enableScroll();
        }
      });
  }

  private disableScroll(): void {
    this.scrollBlock.enable();
  }

  private enableScroll(): void {
    this.scrollBlock.disable();
  }
}
